import { useEffect, useRef, useState } from "hono/jsx";
import { Appearance } from "../shared/types.ts";

// Module-level cache to persist state across remounts/network flickers
let lastValidAppearance: Appearance | null = null;

export function CharacterSelect() {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dragRef = useRef<{x: number, y: number} | null>(null);
  
  // Initialize with current state if available, or defaults
  const getInitialAppearance = (): Appearance => {
      // @ts-ignore: Deno resolution
      const state = (globalThis as any).BABYLON_ATTACK?.state; // Access globally exposed state logic
      
      let found: Appearance | null = null;

      // 1. Try Authoritative State
      if (state && state.myId && state.players && state.players[state.myId]?.appearance) {
          found = { ...state.players[state.myId].appearance };
      }
      // 2. Try Local/Optimistic State
      else if (state && state.localCharacter?.appearance) {
          found = { ...state.localCharacter.appearance };
      }
      
      // 3. Update Cache if found
      if (found) {
          lastValidAppearance = found;
          return found;
      }

      // 4. Fallback to Cache
      if (lastValidAppearance) {
          return { ...lastValidAppearance };
      }
      // 5. Hard Fallback
      return {
        head: "A",
        body: "A",
        legs: "A",
        extras: [],
        uniform: "woodlands"
      };
  };

  const [appearance, setAppearance] = useState<Appearance>(() => getInitialAppearance());

  // Re-sync on open
  const syncState = () => {
    // getInitialAppearance now checks the cache, so this is safe
    const next = getInitialAppearance();
    setAppearance(next);
  };

  const [parts, setParts] = useState<{
    heads: string[];
    bodies: string[];
    legs: string[];
    extras: string[];
  }>({
    heads: ["A", "B", "C", "D"],
    bodies: ["A", "B", "C", "D"],
    legs: ["A", "B", "C", "D"],
    extras: ["mask", "pack", "vest"]
  });

  // Groups
  // Derived Groups (Dynamic from GLB source)
  const backpackGroup = parts.extras.filter(id => id.toLowerCase().includes("pack") || id.toLowerCase().includes("camel"));
  const equipGroup = parts.extras.filter(id => !id.toLowerCase().includes("pack") && !id.toLowerCase().includes("camel"));

  const getLabel = (id: string) => {
    const labels: Record<string, string> = {
        "mask": "Tactical Mask",
        "pack": "Backpack",
        "vest": "Combat Vest",
        "googles": "Night Vision",
        "helmet": "Advanced Helmet",
        "backpack": "Combat Pack",
        "cap": "Command Cap",
        "woodlands": "Woodlands",
        "desert": "Desert Camo",
        "standard": "Standard Army",
        "urban": "Urban Ops",
        "winter": "Winter Frost",
        // New Items
        "extra_backpack": "Heavy Backpack",
        "extra_camelback": "Camelback",
        "extra_light_backpack": "Light Pack",
        "extra_equip_A": "Heavy Rig",
        "extra_equip_B": "Recon Rig",
        "extra_equip_B": "Recon Rig",
        "extra_equip_C": "Medic Rig",
        // Militia Skins
        "militia_a": "Desert",
        "militia_b": "Jungle",
        "militia_c": "Underground",
        "militia_d": "Urban"
    };
    const suffixMatch = id.match(/_([A-Za-z0-9]+)$/);
    if (!labels[id.toLowerCase()] && suffixMatch && suffixMatch[1].length === 1) {
        return suffixMatch[1].toUpperCase();
    }

    return labels[id.toLowerCase()] || id.charAt(0).toUpperCase() + id.slice(1);
  };

  // Track if user has modified state to prevent polling overwrites
  const isDirty = useRef(false);

  useEffect(() => {
    const handleParts = (e: Event) => {
        const discovered = (e as CustomEvent).detail;
        setParts(discovered);
    };
    globalThis.addEventListener("attack:partsDiscovered", handleParts);
    
    // Request parts if already discovered (could be stored on global window too)
    globalThis.dispatchEvent(new CustomEvent("attack:requestParts"));

    // Event-Driven Update: Listen for authoritative state changes
    const handlePlayerUpdate = (e: Event) => {
        // Stop updates if user is actively editing
        if (isDirty.current) return;

        const fresh = (e as CustomEvent).detail.appearance as Appearance;
        
        setAppearance(prev => {
             // Double check dirty inside setter
             if (isDirty.current) return prev;

             if (JSON.stringify(prev) !== JSON.stringify(fresh)) {
                 // Also notify preview
                 globalThis.dispatchEvent(new CustomEvent("attack:updatePreviewVariant", { detail: { appearance: fresh } }));
                 return fresh;
             }
             return prev;
        });
    };
    globalThis.addEventListener("attack:myPlayerUpdated", handlePlayerUpdate);

    const handleOpen = () => {
      const dialog = dialogRef.current || document.getElementById("character-select-dialog") as HTMLDialogElement;
      if (dialog) {
          // Sync with latest state on open
          syncState();
          isDirty.current = false; // Reset dirty state on open
          
          dialog.showModal();
          requestAnimationFrame(() => {
            if (canvasRef.current) {
                // FORCE: Use CURRENT state 'appearance' (which is D1/Desert from cache/state)
                // Do NOT call getInitialAppearance() here because global state might be flaking (empty loop bug).
                // If React state is correct (UI shows D1), then preview MUST match D1.
                globalThis.dispatchEvent(new CustomEvent("attack:startPreview", { 
                    detail: { 
                        canvas: canvasRef.current,
                        appearance: appearance // Use local state variable
                    } 
                }));
            }
          });
      }
    };
    globalThis.addEventListener("attack:openCharacterSelect", handleOpen);
    return () => {
        globalThis.removeEventListener("attack:openCharacterSelect", handleOpen);
        globalThis.removeEventListener("attack:partsDiscovered", handleParts);
        globalThis.removeEventListener("attack:myPlayerUpdated", handlePlayerUpdate);
    };
  }, [appearance]);

  const updateAppearance = (patch: Partial<Appearance>) => {
    console.log("[CharacterSelect] Updating appearance:", patch);
    isDirty.current = true; // Mark as modified by user
    const next = { ...appearance, ...patch };
    setAppearance(next);
    globalThis.dispatchEvent(new CustomEvent("attack:updatePreviewVariant", { detail: { appearance: next } }));
  };

  const toggleExtra = (id: string) => {
    const extras = appearance.extras.includes(id) 
        ? appearance.extras.filter(e => e !== id)
        : [...appearance.extras, id];
    updateAppearance({ extras });
  };

  const setExclusiveExtra = (group: string[], selectedId: string | null) => {
    // 1. Remove all items from this group
    let newExtras = appearance.extras.filter(e => !group.includes(e));
    
    // 2. Add the selected one if not null
    if (selectedId) {
        newExtras.push(selectedId);
    }
    
    updateAppearance({ extras: newExtras });
  };

  const saveConfig = () => {
    globalThis.dispatchEvent(new CustomEvent("attack:saveConfig", { detail: { appearance } }));
    close();
  };

  const close = () => {
    dialogRef.current?.close();
    globalThis.dispatchEvent(new CustomEvent("attack:stopPreview", { detail: { canvas: canvasRef.current } }));
  };
  
  // Derived state for UI
  // Derived state for UI
  const currentBackpack = appearance.extras.find(e => backpackGroup.includes(e)) || null;
  const currentEquip = appearance.extras.find(e => equipGroup.includes(e)) || null;

  return (
    <dialog
      id="character-select-dialog"
      ref={dialogRef}
      className="backdrop:bg-black/80 p-0 rounded-2xl overflow-hidden shadow-2xl border border-white/10"
      style={{
        backgroundColor: "#111",
        color: "white",
        width: "800px",
        maxWidth: "90vw",
        height: "600px",
        maxHeight: "90vh",
        margin: "auto",
      }}
    >
      <div className="flex h-full">
        {/* Preview Area */}
        <div className="flex-1 bg-linear-to-b from-gray-900 to-black relative">
          <canvas 
            ref={canvasRef} 
            className="w-full h-full outline-none touch-none"
            style={{ touchAction: "none" }}
            onPointerDown={(e) => {
                dragRef.current = { x: e.clientX, y: e.clientY };
                (e.target as HTMLElement).setPointerCapture(e.pointerId);
            }}
            onPointerUp={(e) => {
                dragRef.current = null;
                (e.target as HTMLElement).releasePointerCapture(e.pointerId);
            }}
            onPointerMove={(e) => {
                if (dragRef.current) {
                    const dx = e.clientX - dragRef.current.x;
                    const dy = e.clientY - dragRef.current.y;
                    
                    dragRef.current = { x: e.clientX, y: e.clientY };
                    
                    // Dispatch rotation delta
                    globalThis.dispatchEvent(new CustomEvent("attack:rotatePreview", { 
                        detail: { dx, dy } 
                    }));
                }
            }}
          />
          <div className="absolute top-4 left-4 bg-black/50 p-2 rounded text-xs uppercase tracking-widest text-white/50 pointer-events-none select-none">
            Preview Model
          </div>
        </div>

        {/* Controls Area */}
        <div className="w-80 p-8 flex flex-col bg-[#1a1a1a] border-l border-white/5">
          <h2 className="text-3xl font-black mb-8 italic tracking-tighter uppercase text-primary">
            Character
          </h2>
          
          <div className="flex-1 space-y-6 overflow-y-auto pr-2">
            {/* Model Selection (Soldier / Militia) */}
            <div>
              <label className="text-xs font-bold uppercase text-white/40 mb-3 block italic tracking-widest">Force</label>
              <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => updateAppearance({ model: "soldier", uniform: "woodlands" })}
                    className={`h-12 rounded-lg font-black transition-all border ${
                      (appearance.model || "soldier") === "soldier"
                      ? "bg-primary text-primary-content border-primary shadow-lg shadow-primary/20" 
                      : "bg-white/5 border-white/10 hover:bg-white/10"
                    }`}
                  >
                    SOLDIER
                  </button>
                  <button
                    type="button"
                    onClick={() => updateAppearance({ model: "militia", uniform: "militia_a" })}
                    className={`h-12 rounded-lg font-black transition-all border ${
                      appearance.model === "militia"
                      ? "bg-red-600 text-white border-red-500 shadow-lg shadow-red-500/20" 
                      : "bg-white/5 border-white/10 hover:bg-white/10"
                    }`}
                  >
                    MILITIA
                  </button>
              </div>
            </div>

            {/* Head Selection */}
            <div>
              <label className="text-xs font-bold uppercase text-white/40 mb-3 block italic tracking-widest">Head</label>
              <div className="grid grid-cols-4 gap-2">
                {parts.heads.map((h: string) => (
                  <button
                    key={h}
                    type="button"
                    onClick={() => updateAppearance({ head: h })}
                    className={`h-12 rounded-lg font-black transition-all border ${
                      appearance.head === h 
                      ? "bg-primary text-primary-content border-primary shadow-lg shadow-primary/20" 
                      : "bg-white/5 border-white/10 hover:bg-white/10"
                    }`}
                  >
                    {h}
                  </button>
                ))}
              </div>
            </div>

            {/* Body Selection */}
            <div>
              <label className="text-xs font-bold uppercase text-white/40 mb-3 block italic tracking-widest">Body</label>
              <div className="grid grid-cols-4 gap-2">
                {parts.bodies.map((b: string) => (
                  <button
                    key={b}
                    type="button"
                    onClick={() => updateAppearance({ body: b })}
                    className={`h-12 rounded-lg font-black transition-all border ${
                      appearance.body === b 
                      ? "bg-primary text-primary-content border-primary shadow-lg shadow-primary/20" 
                      : "bg-white/5 border-white/10 hover:bg-white/10"
                    }`}
                  >
                    {b}
                  </button>
                ))}
              </div>
            </div>

            {/* Legs Selection */}
            <div>
              <label className="text-xs font-bold uppercase text-white/40 mb-3 block italic tracking-widest">Legs</label>
              <div className="grid grid-cols-4 gap-2">
                {parts.legs.map((l: string) => (
                  <button
                    key={l}
                    type="button"
                    onClick={() => updateAppearance({ legs: l })}
                    className={`h-12 rounded-lg font-black transition-all border ${
                      appearance.legs === l 
                      ? "bg-primary text-primary-content border-primary shadow-lg shadow-primary/20" 
                      : "bg-white/5 border-white/10 hover:bg-white/10"
                    }`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>

            {/* Uniform Selection */}
            <div>
              <label className="text-xs font-bold uppercase text-white/40 mb-3 block italic tracking-widest">
                  {(appearance.model === "militia") ? "Outfit Style" : "Uniform Pattern"}
              </label>
              <div className="grid grid-cols-1 gap-2">
                {((appearance.model === "militia") 
                    ? ["militia_a", "militia_b", "militia_c", "militia_d"]
                    : ["woodlands", "desert", "standard", "urban", "winter"]
                ).map((u: string) => (
                  <button
                    key={u}
                    type="button"
                    onClick={() => updateAppearance({ uniform: u })}
                    className={`h-10 px-4 rounded-lg font-bold text-left transition-all border flex items-center justify-between ${
                      appearance.uniform === u 
                      ? "bg-accent text-accent-content border-accent shadow-lg shadow-accent/20" 
                      : "bg-white/5 border-white/10 hover:bg-white/10"
                    }`}
                  >
                    <span>{getLabel(u)}</span>
                    {appearance.uniform === u && <span className="w-2 h-2 rounded-full bg-accent-content" />}
                  </button>
                ))}
              </div>
            </div>

            {/* Extras: Backpacks (Radio - Dynamic) */}
            {backpackGroup.length > 0 && (
            <div>
              <label className="text-xs font-bold uppercase text-white/40 mb-3 block italic tracking-widest">Backpack</label>
              <div className="grid grid-cols-2 gap-2">
                 <button
                    type="button"
                    onClick={() => setExclusiveExtra(backpackGroup, null)}
                    className={`h-10 px-4 rounded-lg font-bold text-sm transition-all border ${
                      currentBackpack === null
                      ? "bg-white/10 border-white/30"
                      : "bg-white/5 border-white/10 hover:bg-white/10 text-white/50"
                    }`}
                  >
                    None
                  </button>
                {backpackGroup.map((id) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setExclusiveExtra(backpackGroup, id)}
                    className={`h-10 px-4 rounded-lg font-bold text-sm transition-all border ${
                      currentBackpack === id
                      ? "bg-secondary text-secondary-content border-secondary shadow-lg shadow-secondary/20"
                      : "bg-white/5 border-white/10 hover:bg-white/10"
                    }`}
                  >
                    {getLabel(id)}
                  </button>
                ))}
              </div>
            </div>
            )}

            {/* Extras: Gear (Radio - Dynamic) */}
            {equipGroup.length > 0 && (
            <div>
              <label className="text-xs font-bold uppercase text-white/40 mb-3 block italic tracking-widest">Tactical Gear</label>
              <div className="grid grid-cols-2 gap-2">
                 <button
                    type="button"
                    onClick={() => setExclusiveExtra(equipGroup, null)}
                    className={`h-10 px-4 rounded-lg font-bold text-sm transition-all border ${
                      currentEquip === null
                      ? "bg-white/10 border-white/30"
                      : "bg-white/5 border-white/10 hover:bg-white/10 text-white/50"
                    }`}
                  >
                    None
                  </button>
                {equipGroup.map((id) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setExclusiveExtra(equipGroup, id)}
                    className={`h-10 px-4 rounded-lg font-bold text-sm transition-all border ${
                      currentEquip === id
                      ? "bg-secondary text-secondary-content border-secondary shadow-lg shadow-secondary/20"
                      : "bg-white/5 border-white/10 hover:bg-white/10"
                    }`}
                  >
                    {getLabel(id)}
                  </button>
                ))}
              </div>
            </div>
            )}
          </div>

          <div className="space-y-3 mt-8">
            <button 
              type="button"
              onClick={saveConfig}
              className="btn btn-primary w-full h-14 text-lg font-black uppercase tracking-widest"
            >
              Finalize & Deploy
            </button>
            <button 
              type="button"
              onClick={close}
              className="btn btn-ghost w-full"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </dialog>
  );
}
